import React, { createContext, useContext, useEffect, useState } from "react";
import { useAlert } from "./Alert";
import CredentialsDrawer from "../components/CredentialsDrawer";
import { ERROR, LOADING, SUCCESS } from "../constants/common";
import {
  createCredential,
  deleteCredential,
  getCredentials,
  updateCredential,
  getPsypact,
  updatePsypact,
  updateMembership,
  createMembership,
} from "../services/credentials.services";
import ConfirmationModal from "../components/ConfirmationModal";
import { trackClickEvent } from "../mixpanel";

const CredentialsContext = createContext({});

export const CredentialsContextProvider = ({ children }) => {
  const { sendAlert } = useAlert();
  const initialState = {
    fetchStatus: "",
    list: [],
    createStatus: "",
    updateStatus: "",
    deleteStatus: "",
  };

  const [credentailsState, setCredentailsState] = useState(initialState);
  const [activeCredential, setActiveCredential] = useState({});
  const [credentialType, setCredentialType] = useState(null);
  const [open, setOpen] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = (data, type = null) => {
    setActiveCredential(data);
    setCredentialType(type);
    setOpen(true);
    trackClickEvent("Updated Profile Information", {
      component: `Credential ${data?.type ? "Update " : "Add "} drawer`,
      page: "Edit Profile",
    });
  };

  const handleOpenConfirmationModal = () => {
    setOpenConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setOpenConfirmationModal(false);
  };

  const updateCredentialstate = (obj) => {
    setCredentailsState((prevState) => ({
      ...prevState,
      ...obj,
    }));
  };

  const fetchCredentials = async () => {
    updateCredentialstate({ fetchStatus: LOADING });
    try {
      const response = await getCredentials();
      updateCredentialstate({
        list: response.data.data,
        fetchStatus: SUCCESS,
      });
    } catch (error) {
      updateCredentialstate({ fetchStatus: ERROR });
      Promise.reject(error);
    }
  };

  const generateCredential = async (data) => {
    updateCredentialstate({ createStatus: LOADING });
    try {
      await createCredential(data);
      updateCredentialstate({
        createStatus: SUCCESS,
      });
      sendAlert("Created successfully", "success");
    } catch (error) {
      updateCredentialstate({ createStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const generateMembership = async (data) => {
    updateCredentialstate({ createStatus: LOADING });
    try {
      await createMembership(data);
      updateCredentialstate({
        createStatus: SUCCESS,
      });
      sendAlert("Created successfully", "success");
    } catch (error) {
      updateCredentialstate({ createStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editCredential = async (data) => {
    updateCredentialstate({ updateStatus: LOADING });
    try {
      await updateCredential(data);
      updateCredentialstate({
        updateStatus: SUCCESS,
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      updateCredentialstate({ updateStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const editMembership = async (data) => {
    updateCredentialstate({ updateStatus: LOADING });
    try {
      await updateMembership(data);
      updateCredentialstate({
        updateStatus: SUCCESS,
      });
      trackClickEvent("Updated Profile Information", {
        component: `Credential ${data?.type ? "Update " : "Add "} drawer`,
        page: "Edit Profile",
      });
      sendAlert("Updated successfully", "success");
    } catch (error) {
      updateCredentialstate({ updateStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const removeCredential = async (data) => {
    updateCredentialstate({ deleteStatus: LOADING });
    try {
      await deleteCredential(data);
      updateCredentialstate({
        deleteStatus: SUCCESS,
      });
      sendAlert("Removed successfully", "success");
    } catch (error) {
      updateCredentialstate({ deleteStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const removeCredentialFunction = async () => {
    await removeCredential({ credential_id: activeCredential?.id });
  };

  const fetchPsypact = async () => {
    updateCredentialstate({ updateStatus: LOADING });
    try {
      const data = await getPsypact();
      updateCredentialstate({ updateStatus: SUCCESS });
      return data;
    } catch (error) {
      console.error("Error fetching Psypact status:", error);
    }
  };

  const editPsypact = async (data) => {
    updateCredentialstate({ updateStatus: LOADING });
    try {
      await updatePsypact(data);
      updateCredentialstate({ updateStatus: SUCCESS });
    } catch (error) {
      console.error("Error updating Psypact status:", error);
    }
  };

  useEffect(() => {
    if (credentailsState.deleteStatus === "SUCCESS") {
      updateCredentialstate("deleteStatus", "");
      setActiveCredential(undefined);
      handleCloseConfirmationModal();
      handleClose();
      fetchCredentials();
    }
  }, [credentailsState.deleteStatus]);

  const state = {
    ...credentailsState,
    open,
    handleClose,
    handleOpen,
    fetchCredentials,
    fetchPsypact,
    editPsypact,
    generateCredential,
    generateMembership,
    editMembership,
    editCredential,
    removeCredential,
    openConfirmationModal: handleOpenConfirmationModal,
    updateCredentialstate,
  };

  return (
    <CredentialsContext.Provider value={state}>
      {children}

      <CredentialsDrawer
        onClose={handleClose}
        open={open}
        activeCredential={activeCredential}
        credentialType={credentialType}
      />
      <ConfirmationModal
        open={openConfirmationModal}
        onClose={handleCloseConfirmationModal}
        message={`Are you sure you want to delete ${activeCredential?.data?.title}?`}
        deleteFunction={removeCredentialFunction}
        loading={credentailsState.deleteStatus === LOADING}
      />
    </CredentialsContext.Provider>
  );
};

export const useCredentials = () => useContext(CredentialsContext);
